/**
 * Technical indicator formulas — native JavaScript implementations.
 *
 * Every function takes an array of candles [{time, open, high, low, close, volume}]
 * and parameters, and returns an array of data points for Lightweight Charts.
 *
 * IMPORTANT: only computes on the candles provided (visible bars).
 * The caller is responsible for not passing future bars.
 */

// ─── Helpers ───────────────────────────────────────────────────────

function closes(candles) {
  return candles.map((c) => c.close);
}

function highs(candles) {
  return candles.map((c) => c.high);
}

function lows(candles) {
  return candles.map((c) => c.low);
}

function trueRange(candles) {
  const tr = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  return tr;
}

// ─── SMA ───────────────────────────────────────────────────────────

export function sma(candles, { period = 20, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  const result = [];
  let sum = 0;

  for (let i = 0; i < src.length; i++) {
    sum += src[i];
    if (i >= period) sum -= src[i - period];
    if (i >= period - 1) {
      result.push({ time: candles[i].time, value: sum / period });
    }
  }
  return result;
}

// ─── EMA ───────────────────────────────────────────────────────────

export function ema(candles, { period = 20, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  const k = 2 / (period + 1);
  const result = [];

  if (src.length < period) return result;

  // Seed with SMA of first `period` values
  let sum = 0;
  for (let i = 0; i < period; i++) sum += src[i];
  let emaVal = sum / period;
  result.push({ time: candles[period - 1].time, value: emaVal });

  for (let i = period; i < src.length; i++) {
    emaVal = src[i] * k + emaVal * (1 - k);
    result.push({ time: candles[i].time, value: emaVal });
  }
  return result;
}

// ─── WMA ───────────────────────────────────────────────────────────

export function wma(candles, { period = 20, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  const result = [];
  const denom = (period * (period + 1)) / 2;

  for (let i = period - 1; i < src.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) {
      sum += src[i - period + 1 + j] * (j + 1);
    }
    result.push({ time: candles[i].time, value: sum / denom });
  }
  return result;
}

// ─── RSI ───────────────────────────────────────────────────────────

export function rsi(candles, { period = 14 } = {}) {
  const src = closes(candles);
  if (src.length < period + 1) return [];

  const result = [];
  let gainSum = 0;
  let lossSum = 0;

  for (let i = 1; i <= period; i++) {
    const diff = src[i] - src[i - 1];
    if (diff >= 0) gainSum += diff;
    else lossSum -= diff;
  }

  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  let rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  result.push({ time: candles[period].time, value: 100 - 100 / (1 + rs) });

  for (let i = period + 1; i < src.length; i++) {
    const diff = src[i] - src[i - 1];
    const gain = diff >= 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push({ time: candles[i].time, value: 100 - 100 / (1 + rs) });
  }
  return result;
}

// ─── MACD ──────────────────────────────────────────────────────────

export function macd(candles, { fastPeriod = 12, slowPeriod = 26, signalPeriod = 9 } = {}) {
  const fastEma = ema(candles, { period: fastPeriod });
  const slowEma = ema(candles, { period: slowPeriod });

  if (fastEma.length === 0 || slowEma.length === 0) return { macdLine: [], signalLine: [], histogram: [] };

  // Align by time
  const slowMap = new Map(slowEma.map((p) => [p.time, p.value]));
  const macdLine = [];
  for (const fp of fastEma) {
    if (slowMap.has(fp.time)) {
      macdLine.push({ time: fp.time, value: fp.value - slowMap.get(fp.time) });
    }
  }

  if (macdLine.length < signalPeriod) return { macdLine, signalLine: [], histogram: [] };

  // Signal line = EMA of MACD line
  const k = 2 / (signalPeriod + 1);
  let sum = 0;
  for (let i = 0; i < signalPeriod; i++) sum += macdLine[i].value;
  let sigVal = sum / signalPeriod;

  const signalLine = [{ time: macdLine[signalPeriod - 1].time, value: sigVal }];
  const histogram = [{
    time: macdLine[signalPeriod - 1].time,
    value: macdLine[signalPeriod - 1].value - sigVal,
    color: macdLine[signalPeriod - 1].value - sigVal >= 0 ? 'rgba(34,197,94,0.7)' : 'rgba(239,68,68,0.7)',
  }];

  for (let i = signalPeriod; i < macdLine.length; i++) {
    sigVal = macdLine[i].value * k + sigVal * (1 - k);
    signalLine.push({ time: macdLine[i].time, value: sigVal });
    const diff = macdLine[i].value - sigVal;
    histogram.push({
      time: macdLine[i].time,
      value: diff,
      color: diff >= 0 ? 'rgba(34,197,94,0.7)' : 'rgba(239,68,68,0.7)',
    });
  }

  return { macdLine, signalLine, histogram };
}

// ─── Bollinger Bands ───────────────────────────────────────────────

export function bollingerBands(candles, { period = 20, stdDev = 2, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  const upper = [];
  const middle = [];
  const lower = [];

  for (let i = period - 1; i < src.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += src[j];
    const mean = sum / period;

    let sqSum = 0;
    for (let j = i - period + 1; j <= i; j++) sqSum += (src[j] - mean) ** 2;
    const sd = Math.sqrt(sqSum / period);

    const time = candles[i].time;
    middle.push({ time, value: mean });
    upper.push({ time, value: mean + stdDev * sd });
    lower.push({ time, value: mean - stdDev * sd });
  }

  return { upper, middle, lower };
}

// ─── ATR ───────────────────────────────────────────────────────────

export function atr(candles, { period = 14 } = {}) {
  if (candles.length < period + 1) return [];

  const tr = trueRange(candles);
  // tr[0] is not a true TR (no previous close), start from index 1
  const result = [];

  let sum = 0;
  for (let i = 1; i <= period; i++) sum += tr[i];
  let atrVal = sum / period;
  result.push({ time: candles[period].time, value: atrVal });

  for (let i = period + 1; i < tr.length; i++) {
    atrVal = (atrVal * (period - 1) + tr[i]) / period;
    result.push({ time: candles[i].time, value: atrVal });
  }
  return result;
}

// ─── Stochastic ────────────────────────────────────────────────────

export function stochastic(candles, { kPeriod = 14, dPeriod = 3, smooth = 3 } = {}) {
  const h = highs(candles);
  const l = lows(candles);
  const c = closes(candles);

  if (candles.length < kPeriod) return { k: [], d: [] };

  // Raw %K
  const rawK = [];
  for (let i = kPeriod - 1; i < candles.length; i++) {
    let hh = -Infinity;
    let ll = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (h[j] > hh) hh = h[j];
      if (l[j] < ll) ll = l[j];
    }
    const range = hh - ll;
    rawK.push(range === 0 ? 50 : ((c[i] - ll) / range) * 100);
  }

  // Smoothed %K (SMA of rawK)
  const kLine = [];
  for (let i = smooth - 1; i < rawK.length; i++) {
    let sum = 0;
    for (let j = i - smooth + 1; j <= i; j++) sum += rawK[j];
    kLine.push({
      time: candles[kPeriod - 1 + i].time,
      value: sum / smooth,
    });
  }

  // %D (SMA of %K)
  const dLine = [];
  for (let i = dPeriod - 1; i < kLine.length; i++) {
    let sum = 0;
    for (let j = i - dPeriod + 1; j <= i; j++) sum += kLine[j].value;
    dLine.push({ time: kLine[i].time, value: sum / dPeriod });
  }

  return { k: kLine, d: dLine };
}

// ─── ADX ───────────────────────────────────────────────────────────

export function adx(candles, { period = 14 } = {}) {
  if (candles.length < period * 2) return { adx: [], plusDI: [], minusDI: [] };

  const h = highs(candles);
  const l = lows(candles);
  const tr = trueRange(candles);

  // +DM and -DM
  const plusDM = [0];
  const minusDM = [0];
  for (let i = 1; i < candles.length; i++) {
    const upMove = h[i] - h[i - 1];
    const downMove = l[i - 1] - l[i];
    plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
    minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
  }

  // Smoothed TR, +DM, -DM (Wilder's smoothing)
  // Start from index 1 since tr[0] and plusDM[0]/minusDM[0] are not valid
  let smoothTR = 0;
  let smoothPlusDM = 0;
  let smoothMinusDM = 0;
  for (let i = 1; i <= period; i++) {
    smoothTR += tr[i];
    smoothPlusDM += plusDM[i];
    smoothMinusDM += minusDM[i];
  }

  const plusDIArr = [];
  const minusDIArr = [];
  const dxArr = [];

  for (let i = period + 1; i < candles.length; i++) {
    smoothTR = smoothTR - smoothTR / period + tr[i];
    smoothPlusDM = smoothPlusDM - smoothPlusDM / period + plusDM[i];
    smoothMinusDM = smoothMinusDM - smoothMinusDM / period + minusDM[i];

    const pdi = smoothTR === 0 ? 0 : (smoothPlusDM / smoothTR) * 100;
    const mdi = smoothTR === 0 ? 0 : (smoothMinusDM / smoothTR) * 100;
    const diSum = pdi + mdi;
    const dx = diSum === 0 ? 0 : (Math.abs(pdi - mdi) / diSum) * 100;

    plusDIArr.push({ time: candles[i].time, value: pdi });
    minusDIArr.push({ time: candles[i].time, value: mdi });
    dxArr.push(dx);
  }

  // ADX = smoothed DX (Wilder's)
  if (dxArr.length < period) return { adx: [], plusDI: plusDIArr, minusDI: minusDIArr };

  let adxVal = 0;
  for (let i = 0; i < period; i++) adxVal += dxArr[i];
  adxVal /= period;

  const adxLine = [{ time: plusDIArr[period - 1].time, value: adxVal }];
  for (let i = period; i < dxArr.length; i++) {
    adxVal = (adxVal * (period - 1) + dxArr[i]) / period;
    adxLine.push({ time: plusDIArr[i].time, value: adxVal });
  }

  return { adx: adxLine, plusDI: plusDIArr, minusDI: minusDIArr };
}

// ─── VWAP ──────────────────────────────────────────────────────────

export function vwap(candles) {
  const result = [];
  let cumTPV = 0;
  let cumVol = 0;
  let currentDay = null;

  for (const c of candles) {
    const date = new Date(c.time * 1000);
    const day = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;

    if (day !== currentDay) {
      cumTPV = 0;
      cumVol = 0;
      currentDay = day;
    }

    const tp = (c.high + c.low + c.close) / 3;
    cumTPV += tp * c.volume;
    cumVol += c.volume;

    result.push({
      time: c.time,
      value: cumVol === 0 ? tp : cumTPV / cumVol,
    });
  }
  return result;
}

// ─── Ichimoku ──────────────────────────────────────────────────────

function midpoint(candles, start, length) {
  let hh = -Infinity;
  let ll = Infinity;
  for (let i = start; i < start + length && i < candles.length; i++) {
    if (candles[i].high > hh) hh = candles[i].high;
    if (candles[i].low < ll) ll = candles[i].low;
  }
  return (hh + ll) / 2;
}

export function ichimoku(
  candles,
  { tenkanPeriod = 9, kijunPeriod = 26, senkouBPeriod = 52, displacement = 26 } = {}
) {
  const tenkanSen = [];
  const kijunSen = [];
  const senkouA = [];
  const senkouB = [];
  const chikouSpan = [];

  // Time interval for extrapolating future timestamps
  const interval = candles.length >= 2 ? candles[1].time - candles[0].time : 60;

  // Helper: get time at index (extrapolate beyond candles.length)
  const timeAt = (idx) => idx < candles.length
    ? candles[idx].time
    : candles[candles.length - 1].time + (idx - candles.length + 1) * interval;

  for (let i = 0; i < candles.length; i++) {
    // Tenkan-sen
    if (i >= tenkanPeriod - 1) {
      tenkanSen.push({
        time: candles[i].time,
        value: midpoint(candles, i - tenkanPeriod + 1, tenkanPeriod),
      });
    }

    // Kijun-sen
    if (i >= kijunPeriod - 1) {
      kijunSen.push({
        time: candles[i].time,
        value: midpoint(candles, i - kijunPeriod + 1, kijunPeriod),
      });
    }

    // Chikou Span (close displaced back by displacement periods)
    if (i >= displacement) {
      chikouSpan.push({
        time: candles[i - displacement].time,
        value: candles[i].close,
      });
    }
  }

  // Senkou Span A & B — displaced FORWARD by displacement periods
  for (let i = 0; i < candles.length; i++) {
    const futureTime = timeAt(i + displacement);

    if (i >= kijunPeriod - 1 && i >= tenkanPeriod - 1) {
      const tenkan = midpoint(candles, i - tenkanPeriod + 1, tenkanPeriod);
      const kijun = midpoint(candles, i - kijunPeriod + 1, kijunPeriod);
      senkouA.push({ time: futureTime, value: (tenkan + kijun) / 2 });
    }
    if (i >= senkouBPeriod - 1) {
      senkouB.push({
        time: futureTime,
        value: midpoint(candles, i - senkouBPeriod + 1, senkouBPeriod),
      });
    }
  }

  // Cloud fill: generate cloudUp (A > B) and cloudDown (B > A) for area rendering
  const aMap = new Map(senkouA.map((p) => [p.time, p.value]));
  const bMap = new Map(senkouB.map((p) => [p.time, p.value]));
  const allTimes = [...new Set([...senkouA.map((p) => p.time), ...senkouB.map((p) => p.time)])].sort((a, b) => a - b);

  const cloudUp = [];   // filled when A >= B (bullish cloud)
  const cloudDown = [];  // filled when B > A (bearish cloud)

  for (const t of allTimes) {
    const a = aMap.get(t);
    const b = bMap.get(t);
    if (a !== undefined && b !== undefined) {
      if (a >= b) {
        cloudUp.push({ time: t, value: a });
        cloudDown.push({ time: t, value: b });
      } else {
        cloudUp.push({ time: t, value: b });
        cloudDown.push({ time: t, value: a });
      }
    }
  }

  return { tenkanSen, kijunSen, senkouA, senkouB, chikouSpan, cloudUp, cloudDown };
}

// ─── Volume (already built into chart, but expose for standalone panel) ───

export function volumeHistogram(candles) {
  return candles.map((c) => ({
    time: c.time,
    value: c.volume,
    color: c.close >= c.open ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)',
  }));
}

// ─── Stochastic RSI ───────────────────────────────────────────────

export function stochRsi(candles, { rsiPeriod = 14, stochPeriod = 14, kSmooth = 3, dSmooth = 3 } = {}) {
  const rsiData = rsi(candles, { period: rsiPeriod });
  if (rsiData.length < stochPeriod) return { k: [], d: [] };

  const rsiValues = rsiData.map((p) => p.value);
  const rsiTimes = rsiData.map((p) => p.time);

  // Apply Stochastic on RSI values
  const rawK = [];
  for (let i = stochPeriod - 1; i < rsiValues.length; i++) {
    let hh = -Infinity, ll = Infinity;
    for (let j = i - stochPeriod + 1; j <= i; j++) {
      if (rsiValues[j] > hh) hh = rsiValues[j];
      if (rsiValues[j] < ll) ll = rsiValues[j];
    }
    const range = hh - ll;
    rawK.push({ time: rsiTimes[i], value: range === 0 ? 50 : ((rsiValues[i] - ll) / range) * 100 });
  }

  // Smooth %K
  const kLine = [];
  for (let i = kSmooth - 1; i < rawK.length; i++) {
    let sum = 0;
    for (let j = i - kSmooth + 1; j <= i; j++) sum += rawK[j].value;
    kLine.push({ time: rawK[i].time, value: sum / kSmooth });
  }

  // %D = SMA of %K
  const dLine = [];
  for (let i = dSmooth - 1; i < kLine.length; i++) {
    let sum = 0;
    for (let j = i - dSmooth + 1; j <= i; j++) sum += kLine[j].value;
    dLine.push({ time: kLine[i].time, value: sum / dSmooth });
  }

  return { k: kLine, d: dLine };
}

// ─── Donchian Channel ─────────────────────────────────────────────

export function donchianChannel(candles, { period = 20 } = {}) {
  if (candles.length < period) return { upper: [], middle: [], lower: [] };

  const upper = [], middle = [], lower = [];
  for (let i = period - 1; i < candles.length; i++) {
    let hh = -Infinity, ll = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (candles[j].high > hh) hh = candles[j].high;
      if (candles[j].low < ll) ll = candles[j].low;
    }
    upper.push({ time: candles[i].time, value: hh });
    lower.push({ time: candles[i].time, value: ll });
    middle.push({ time: candles[i].time, value: (hh + ll) / 2 });
  }
  return { upper, middle, lower };
}

// ─── Hull Moving Average (HMA) ───────────────────────────────────

export function hma(candles, { period = 20, source = 'close' } = {}) {
  const halfPeriod = Math.max(1, Math.floor(period / 2));
  const sqrtPeriod = Math.max(1, Math.round(Math.sqrt(period)));

  const wma1 = wma(candles, { period: halfPeriod, source });
  const wma2 = wma(candles, { period, source });
  if (wma1.length === 0 || wma2.length === 0) return [];

  // Align by time: 2*WMA(half) - WMA(full)
  const wma2Map = new Map(wma2.map((p) => [p.time, p.value]));
  const diffCandles = [];
  for (const p of wma1) {
    const v2 = wma2Map.get(p.time);
    if (v2 !== undefined) {
      diffCandles.push({ time: p.time, close: 2 * p.value - v2 });
    }
  }

  // Final WMA of sqrt(period) on the diff
  return wma(diffCandles, { period: sqrtPeriod });
}

// ─── Aroon ────────────────────────────────────────────────────────

export function aroon(candles, { period = 25 } = {}) {
  if (candles.length < period + 1) return { up: [], down: [], oscillator: [] };

  const up = [], down = [], oscillator = [];

  for (let i = period; i < candles.length; i++) {
    let highIdx = 0, lowIdx = 0;
    let hh = -Infinity, ll = Infinity;
    for (let j = 0; j <= period; j++) {
      const idx = i - period + j;
      if (candles[idx].high >= hh) { hh = candles[idx].high; highIdx = j; }
      if (candles[idx].low <= ll) { ll = candles[idx].low; lowIdx = j; }
    }
    const aroonUp = (highIdx / period) * 100;
    const aroonDown = (lowIdx / period) * 100;
    up.push({ time: candles[i].time, value: aroonUp });
    down.push({ time: candles[i].time, value: aroonDown });
    oscillator.push({ time: candles[i].time, value: aroonUp - aroonDown });
  }
  return { up, down, oscillator };
}

// ─── Awesome Oscillator ──────────────────────────────────────────

export function awesomeOscillator(candles, { fastPeriod = 5, slowPeriod = 34 } = {}) {
  if (candles.length < slowPeriod) return [];

  const result = [];
  for (let i = slowPeriod - 1; i < candles.length; i++) {
    let fastSum = 0, slowSum = 0;
    for (let j = i - fastPeriod + 1; j <= i; j++) {
      fastSum += (candles[j].high + candles[j].low) / 2;
    }
    for (let j = i - slowPeriod + 1; j <= i; j++) {
      slowSum += (candles[j].high + candles[j].low) / 2;
    }
    const val = fastSum / fastPeriod - slowSum / slowPeriod;
    result.push({
      time: candles[i].time,
      value: val,
      color: result.length > 0 && val >= result[result.length - 1].value
        ? 'rgba(34,197,94,0.7)' : 'rgba(239,68,68,0.7)',
    });
  }
  // Fix first bar color
  if (result.length > 0) result[0].color = result[0].value >= 0 ? 'rgba(34,197,94,0.7)' : 'rgba(239,68,68,0.7)';
  return result;
}

// ─── TRIX ─────────────────────────────────────────────────────────

export function trix(candles, { period = 18 } = {}) {
  const ema1 = ema(candles, { period });
  if (ema1.length < period) return [];
  const pseudo1 = ema1.map((p) => ({ time: p.time, close: p.value }));
  const ema2 = ema(pseudo1, { period });
  if (ema2.length < period) return [];
  const pseudo2 = ema2.map((p) => ({ time: p.time, close: p.value }));
  const ema3 = ema(pseudo2, { period });
  if (ema3.length < 2) return [];

  const result = [];
  for (let i = 1; i < ema3.length; i++) {
    const prev = ema3[i - 1].value;
    result.push({
      time: ema3[i].time,
      value: prev === 0 ? 0 : ((ema3[i].value - prev) / prev) * 10000,
    });
  }
  return result;
}

// ─── Accumulation/Distribution Line ──────────────────────────────

export function adLine(candles) {
  if (candles.length === 0) return [];

  const result = [];
  let cumAD = 0;
  for (const c of candles) {
    const range = c.high - c.low;
    const clv = range === 0 ? 0 : ((c.close - c.low) - (c.high - c.close)) / range;
    cumAD += clv * c.volume;
    result.push({ time: c.time, value: cumAD });
  }
  return result;
}

// ─── Chaikin Oscillator ──────────────────────────────────────────

export function chaikinOscillator(candles, { fastPeriod = 3, slowPeriod = 10 } = {}) {
  const ad = adLine(candles);
  if (ad.length === 0) return [];

  const adCandles = ad.map((p) => ({ time: p.time, close: p.value }));
  const fastEma = ema(adCandles, { period: fastPeriod });
  const slowEma = ema(adCandles, { period: slowPeriod });

  const slowMap = new Map(slowEma.map((p) => [p.time, p.value]));
  const result = [];
  for (const f of fastEma) {
    const s = slowMap.get(f.time);
    if (s !== undefined) {
      result.push({ time: f.time, value: f.value - s });
    }
  }
  return result;
}

// ─── ADXR (Average Directional Movement Rating) ──────────────────

export function adxr(candles, { period = 14 } = {}) {
  const adxData = adx(candles, { period });
  if (adxData.adx.length < period) return { adxr: [], plusDI: adxData.plusDI, minusDI: adxData.minusDI };

  const result = [];
  for (let i = period; i < adxData.adx.length; i++) {
    result.push({
      time: adxData.adx[i].time,
      value: (adxData.adx[i].value + adxData.adx[i - period].value) / 2,
    });
  }
  return { adxr: result, plusDI: adxData.plusDI, minusDI: adxData.minusDI };
}

// ─── KST (Know Sure Thing) ───────────────────────────────────────

export function kst(candles, {
  roc1 = 10, roc2 = 15, roc3 = 20, roc4 = 30,
  sma1 = 10, sma2 = 10, sma3 = 10, sma4 = 15,
  signalPeriod = 9,
} = {}) {
  const c = closes(candles);
  if (c.length < roc4 + sma4) return { kst: [], signal: [] };

  // ROC values
  const rocVal = (arr, per, i) => arr[i - per] === 0 ? 0 : ((arr[i] - arr[i - per]) / arr[i - per]) * 100;

  // Compute smoothed ROCs
  const smoothRoc = (per, smaPer) => {
    const vals = [];
    for (let i = per; i < c.length; i++) {
      vals.push({ idx: i, value: rocVal(c, per, i) });
    }
    // SMA of ROC
    const result = [];
    for (let i = smaPer - 1; i < vals.length; i++) {
      let sum = 0;
      for (let j = i - smaPer + 1; j <= i; j++) sum += vals[j].value;
      result.push({ idx: vals[i].idx, value: sum / smaPer });
    }
    return result;
  };

  const sr1 = smoothRoc(roc1, sma1);
  const sr2 = smoothRoc(roc2, sma2);
  const sr3 = smoothRoc(roc3, sma3);
  const sr4 = smoothRoc(roc4, sma4);

  // Align by idx, KST = sr1*1 + sr2*2 + sr3*3 + sr4*4
  const maps = [sr1, sr2, sr3, sr4].map((arr) => new Map(arr.map((p) => [p.idx, p.value])));
  const kstLine = [];
  for (let i = 0; i < c.length; i++) {
    const v1 = maps[0].get(i), v2 = maps[1].get(i), v3 = maps[2].get(i), v4 = maps[3].get(i);
    if (v1 !== undefined && v2 !== undefined && v3 !== undefined && v4 !== undefined) {
      kstLine.push({ time: candles[i].time, value: v1 + v2 * 2 + v3 * 3 + v4 * 4 });
    }
  }

  // Signal = SMA of KST
  const signal = [];
  for (let i = signalPeriod - 1; i < kstLine.length; i++) {
    let sum = 0;
    for (let j = i - signalPeriod + 1; j <= i; j++) sum += kstLine[j].value;
    signal.push({ time: kstLine[i].time, value: sum / signalPeriod });
  }

  return { kst: kstLine, signal };
}

// ─── Linear Regression Channel ───────────────────────────────────

export function linearRegressionChannel(candles, { period = 100, deviations = 2 } = {}) {
  if (candles.length < period) return { upper: [], middle: [], lower: [] };

  const c = closes(candles);
  const upper = [], middle = [], lower = [];

  for (let i = period - 1; i < c.length; i++) {
    // Linear regression over window
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    for (let j = 0; j < period; j++) {
      const y = c[i - period + 1 + j];
      sumX += j;
      sumY += y;
      sumXY += j * y;
      sumX2 += j * j;
    }
    const n = period;
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const regValue = intercept + slope * (period - 1);

    // Standard error
    let sumSqErr = 0;
    for (let j = 0; j < period; j++) {
      const predicted = intercept + slope * j;
      const actual = c[i - period + 1 + j];
      sumSqErr += (actual - predicted) ** 2;
    }
    const stdErr = Math.sqrt(sumSqErr / n);

    middle.push({ time: candles[i].time, value: regValue });
    upper.push({ time: candles[i].time, value: regValue + deviations * stdErr });
    lower.push({ time: candles[i].time, value: regValue - deviations * stdErr });
  }
  return { upper, middle, lower };
}

// ─── Standard Deviation ──────────────────────────────────────────

export function stdDev(candles, { period = 20, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  if (src.length < period) return [];

  const result = [];
  for (let i = period - 1; i < src.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += src[j];
    const mean = sum / period;
    let sqSum = 0;
    for (let j = i - period + 1; j <= i; j++) sqSum += (src[j] - mean) ** 2;
    result.push({ time: candles[i].time, value: Math.sqrt(sqSum / period) });
  }
  return result;
}

// ─── Elder-Ray (Bull/Bear Power) ─────────────────────────────────

export function elderRay(candles, { period = 13 } = {}) {
  const emaData = ema(candles, { period });
  if (emaData.length === 0) return { bull: [], bear: [] };

  const emaMap = new Map(emaData.map((p) => [p.time, p.value]));
  const bull = [], bear = [];

  for (const c of candles) {
    const e = emaMap.get(c.time);
    if (e !== undefined) {
      bull.push({ time: c.time, value: c.high - e });
      bear.push({ time: c.time, value: c.low - e });
    }
  }
  return { bull, bear };
}

// ─── Coppock Curve ───────────────────────────────────────────────

export function coppockCurve(candles, { longRoc = 14, shortRoc = 11, wmaPeriod = 10 } = {}) {
  const c = closes(candles);
  if (c.length <= longRoc + wmaPeriod) return [];

  // Sum of two ROCs
  const rocSum = [];
  for (let i = longRoc; i < c.length; i++) {
    const lr = c[i - longRoc] === 0 ? 0 : ((c[i] - c[i - longRoc]) / c[i - longRoc]) * 100;
    const sr = i >= shortRoc && c[i - shortRoc] !== 0
      ? ((c[i] - c[i - shortRoc]) / c[i - shortRoc]) * 100 : 0;
    rocSum.push({ idx: i, value: lr + sr });
  }

  // WMA of ROC sum
  const denom = (wmaPeriod * (wmaPeriod + 1)) / 2;
  const result = [];
  for (let i = wmaPeriod - 1; i < rocSum.length; i++) {
    let sum = 0;
    for (let j = 0; j < wmaPeriod; j++) {
      sum += rocSum[i - wmaPeriod + 1 + j].value * (j + 1);
    }
    result.push({ time: candles[rocSum[i].idx].time, value: sum / denom });
  }
  return result;
}

// ─── McGinley Dynamic ────────────────────────────────────────────

export function mcginleyDynamic(candles, { period = 14, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  if (src.length < period) return [];

  const result = [];
  // Seed with SMA
  let sum = 0;
  for (let i = 0; i < period; i++) sum += src[i];
  let md = sum / period;
  result.push({ time: candles[period - 1].time, value: md });

  for (let i = period; i < src.length; i++) {
    const ratio = src[i] / md;
    md = md + (src[i] - md) / (period * Math.pow(ratio, 4));
    result.push({ time: candles[i].time, value: md });
  }
  return result;
}

// ─── Zig Zag ─────────────────────────────────────────────────────

export function zigzag(candles, { deviation = 5 } = {}) {
  if (candles.length < 2) return [];

  const threshold = deviation / 100;
  const pivots = [{ time: candles[0].time, value: candles[0].close, idx: 0 }];
  let lastDir = 0; // 0=unknown, 1=up, -1=down
  let lastPivotHigh = candles[0].high;
  let lastPivotLow = candles[0].low;
  let lastPivotIdx = 0;

  for (let i = 1; i < candles.length; i++) {
    const c = candles[i];

    if (lastDir >= 0 && c.high >= lastPivotHigh) {
      lastPivotHigh = c.high;
      lastPivotIdx = i;
      pivots[pivots.length - 1] = { time: c.time, value: c.high, idx: i };
    } else if (lastDir <= 0 && c.low <= lastPivotLow) {
      lastPivotLow = c.low;
      lastPivotIdx = i;
      pivots[pivots.length - 1] = { time: c.time, value: c.low, idx: i };
    }

    if (lastDir >= 0 && c.low <= lastPivotHigh * (1 - threshold)) {
      pivots.push({ time: c.time, value: c.low, idx: i });
      lastPivotLow = c.low;
      lastPivotIdx = i;
      lastDir = -1;
    } else if (lastDir <= 0 && c.high >= lastPivotLow * (1 + threshold)) {
      pivots.push({ time: c.time, value: c.high, idx: i });
      lastPivotHigh = c.high;
      lastPivotIdx = i;
      lastDir = 1;
    }
  }

  return pivots;
}

// ─── DEMA (Double Exponential Moving Average) ─────────────────────

export function dema(candles, { period = 20, source = 'close' } = {}) {
  const ema1 = ema(candles, { period, source });
  if (ema1.length < period) return [];

  // Build pseudo-candles from ema1 for second EMA pass
  const ema1Map = new Map(ema1.map((p) => [p.time, p.value]));
  const pseudoCandles = ema1.map((p) => ({ time: p.time, close: p.value }));
  const ema2 = ema(pseudoCandles, { period });
  const ema2Map = new Map(ema2.map((p) => [p.time, p.value]));

  const result = [];
  for (const p of ema1) {
    const e2 = ema2Map.get(p.time);
    if (e2 !== undefined) {
      result.push({ time: p.time, value: 2 * p.value - e2 });
    }
  }
  return result;
}

// ─── TEMA (Triple Exponential Moving Average) ─────────────────────

export function tema(candles, { period = 20, source = 'close' } = {}) {
  const ema1 = ema(candles, { period, source });
  if (ema1.length < period) return [];

  const pseudo1 = ema1.map((p) => ({ time: p.time, close: p.value }));
  const ema2 = ema(pseudo1, { period });
  if (ema2.length < period) return [];

  const pseudo2 = ema2.map((p) => ({ time: p.time, close: p.value }));
  const ema3 = ema(pseudo2, { period });

  const e1Map = new Map(ema1.map((p) => [p.time, p.value]));
  const e2Map = new Map(ema2.map((p) => [p.time, p.value]));

  const result = [];
  for (const p of ema3) {
    const v1 = e1Map.get(p.time);
    const v2 = e2Map.get(p.time);
    if (v1 !== undefined && v2 !== undefined) {
      result.push({ time: p.time, value: 3 * v1 - 3 * v2 + p.value });
    }
  }
  return result;
}

// ─── SMMA (Smoothed Moving Average / RMA) ─────────────────────────

export function smma(candles, { period = 20, source = 'close' } = {}) {
  const src = source === 'close' ? closes(candles) : candles.map((c) => c[source]);
  if (src.length < period) return [];

  const result = [];
  let sum = 0;
  for (let i = 0; i < period; i++) sum += src[i];
  let val = sum / period;
  result.push({ time: candles[period - 1].time, value: val });

  for (let i = period; i < src.length; i++) {
    val = (val * (period - 1) + src[i]) / period;
    result.push({ time: candles[i].time, value: val });
  }
  return result;
}

// ─── Supertrend ───────────────────────────────────────────────────

export function supertrend(candles, { period = 10, multiplier = 3 } = {}) {
  if (candles.length < period + 1) return { line: [], direction: [] };

  const atrData = atr(candles, { period });
  if (atrData.length === 0) return { line: [], direction: [] };

  const atrMap = new Map(atrData.map((p) => [p.time, p.value]));
  const result = [];

  let prevUpper = 0, prevLower = 0, prevDir = 1;

  for (let i = period; i < candles.length; i++) {
    const c = candles[i];
    const atrVal = atrMap.get(c.time);
    if (atrVal === undefined) continue;

    const hl2 = (c.high + c.low) / 2;
    let upper = hl2 + multiplier * atrVal;
    let lower = hl2 - multiplier * atrVal;

    // Adjust bands based on previous values
    if (i > period) {
      lower = lower > prevLower || candles[i - 1].close < prevLower ? lower : prevLower;
      upper = upper < prevUpper || candles[i - 1].close > prevUpper ? upper : prevUpper;
    }

    // Direction flip using previous direction flag (not float equality)
    let dir;
    if (prevDir === -1) {
      dir = c.close > upper ? 1 : -1;
    } else {
      dir = c.close < lower ? -1 : 1;
    }

    const st = dir === 1 ? lower : upper;
    result.push({
      time: c.time,
      value: st,
      color: dir === 1 ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.9)',
    });

    prevUpper = upper;
    prevLower = lower;
    prevDir = dir;
  }
  return { line: result };
}

// ─── Parabolic SAR ────────────────────────────────────────────────

export function parabolicSar(candles, { step = 0.02, max = 0.2 } = {}) {
  if (candles.length < 2) return [];

  const result = [];
  let af = step;
  let isLong = candles[1].close > candles[0].close;
  let sar = isLong ? candles[0].low : candles[0].high;
  let ep = isLong ? candles[1].high : candles[1].low;

  result.push({ time: candles[1].time, value: sar });

  for (let i = 2; i < candles.length; i++) {
    const prev = candles[i - 1];
    const curr = candles[i];

    sar = sar + af * (ep - sar);

    if (isLong) {
      sar = Math.min(sar, prev.low, candles[i - 2].low);
      if (curr.low < sar) {
        isLong = false;
        sar = ep;
        ep = curr.low;
        af = step;
      } else {
        if (curr.high > ep) {
          ep = curr.high;
          af = Math.min(af + step, max);
        }
      }
    } else {
      sar = Math.max(sar, prev.high, candles[i - 2].high);
      if (curr.high > sar) {
        isLong = true;
        sar = ep;
        ep = curr.high;
        af = step;
      } else {
        if (curr.low < ep) {
          ep = curr.low;
          af = Math.min(af + step, max);
        }
      }
    }

    result.push({ time: curr.time, value: sar });
  }
  return result;
}

// ─── Keltner Channel ──────────────────────────────────────────────

export function keltnerChannel(candles, { period = 20, atrPeriod = 10, multiplier = 1.5 } = {}) {
  const emaData = ema(candles, { period });
  const atrData = atr(candles, { period: atrPeriod });
  if (emaData.length === 0 || atrData.length === 0) return { upper: [], middle: [], lower: [] };

  const atrMap = new Map(atrData.map((p) => [p.time, p.value]));
  const upper = [], middle = [], lower = [];

  for (const e of emaData) {
    const a = atrMap.get(e.time);
    if (a !== undefined) {
      middle.push({ time: e.time, value: e.value });
      upper.push({ time: e.time, value: e.value + multiplier * a });
      lower.push({ time: e.time, value: e.value - multiplier * a });
    }
  }
  return { upper, middle, lower };
}

// ─── Pivot Points ─────────────────────────────────────────────────

export function pivotPoints(candles) {
  if (candles.length < 2) return { pivot: [], r1: [], r2: [], r3: [], s1: [], s2: [], s3: [] };

  // Group by day, use previous day's HLC for pivot
  const pivot = [], r1 = [], r2 = [], r3 = [], s1 = [], s2 = [], s3 = [];
  let prevDayH = null, prevDayL = null, prevDayC = null;
  let currentDay = null;
  let dayH = -Infinity, dayL = Infinity, dayC = 0;

  for (const c of candles) {
    const date = new Date(c.time * 1000);
    const day = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;

    if (day !== currentDay) {
      if (currentDay !== null) {
        prevDayH = dayH;
        prevDayL = dayL;
        prevDayC = dayC;
      }
      currentDay = day;
      dayH = c.high;
      dayL = c.low;
    }
    dayH = Math.max(dayH, c.high);
    dayL = Math.min(dayL, c.low);
    dayC = c.close;

    if (prevDayH !== null) {
      const pp = (prevDayH + prevDayL + prevDayC) / 3;
      pivot.push({ time: c.time, value: pp });
      r1.push({ time: c.time, value: 2 * pp - prevDayL });
      s1.push({ time: c.time, value: 2 * pp - prevDayH });
      r2.push({ time: c.time, value: pp + (prevDayH - prevDayL) });
      s2.push({ time: c.time, value: pp - (prevDayH - prevDayL) });
      r3.push({ time: c.time, value: prevDayH + 2 * (pp - prevDayL) });
      s3.push({ time: c.time, value: prevDayL - 2 * (prevDayH - pp) });
    }
  }
  return { pivot, r1, r2, r3, s1, s2, s3 };
}

// ─── CCI (Commodity Channel Index) ────────────────────────────────

export function cci(candles, { period = 20 } = {}) {
  if (candles.length < period) return [];

  const result = [];
  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    const tps = [];
    for (let j = i - period + 1; j <= i; j++) {
      const tp = (candles[j].high + candles[j].low + candles[j].close) / 3;
      tps.push(tp);
      sum += tp;
    }
    const mean = sum / period;
    let meanDev = 0;
    for (const tp of tps) meanDev += Math.abs(tp - mean);
    meanDev /= period;

    const val = meanDev === 0 ? 0 : (tps[tps.length - 1] - mean) / (0.015 * meanDev);
    result.push({ time: candles[i].time, value: val });
  }
  return result;
}

// ─── MFI (Money Flow Index) ───────────────────────────────────────

export function mfi(candles, { period = 14 } = {}) {
  if (candles.length < period + 1) return [];

  const result = [];
  const tp = candles.map((c) => (c.high + c.low + c.close) / 3);
  const mf = tp.map((t, i) => t * candles[i].volume);

  for (let i = period; i < candles.length; i++) {
    let posFlow = 0, negFlow = 0;
    for (let j = i - period + 1; j <= i; j++) {
      if (tp[j] > tp[j - 1]) posFlow += mf[j];
      else negFlow += mf[j];
    }
    const ratio = negFlow === 0 ? 100 : posFlow / negFlow;
    result.push({ time: candles[i].time, value: 100 - 100 / (1 + ratio) });
  }
  return result;
}

// ─── OBV (On Balance Volume) ──────────────────────────────────────

export function obv(candles) {
  if (candles.length === 0) return [];

  const result = [{ time: candles[0].time, value: candles[0].volume }];
  let cumOBV = candles[0].volume;

  for (let i = 1; i < candles.length; i++) {
    if (candles[i].close > candles[i - 1].close) cumOBV += candles[i].volume;
    else if (candles[i].close < candles[i - 1].close) cumOBV -= candles[i].volume;
    result.push({ time: candles[i].time, value: cumOBV });
  }
  return result;
}

// ─── Williams %R ──────────────────────────────────────────────────

export function williamsR(candles, { period = 14 } = {}) {
  if (candles.length < period) return [];

  const h = highs(candles);
  const l = lows(candles);
  const c = closes(candles);
  const result = [];

  for (let i = period - 1; i < candles.length; i++) {
    let hh = -Infinity, ll = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (h[j] > hh) hh = h[j];
      if (l[j] < ll) ll = l[j];
    }
    const range = hh - ll;
    const val = range === 0 ? -50 : ((hh - c[i]) / range) * -100;
    result.push({ time: candles[i].time, value: val });
  }
  return result;
}

// ─── ROC (Rate of Change) ─────────────────────────────────────────

export function roc(candles, { period = 12 } = {}) {
  if (candles.length <= period) return [];

  const c = closes(candles);
  const result = [];

  for (let i = period; i < candles.length; i++) {
    const prev = c[i - period];
    const val = prev === 0 ? 0 : ((c[i] - prev) / prev) * 100;
    result.push({ time: candles[i].time, value: val });
  }
  return result;
}

// ─── Momentum ─────────────────────────────────────────────────────

export function momentum(candles, { period = 10 } = {}) {
  if (candles.length <= period) return [];

  const c = closes(candles);
  const result = [];

  for (let i = period; i < candles.length; i++) {
    result.push({ time: candles[i].time, value: c[i] - c[i - period] });
  }
  return result;
}

// ─── CMF (Chaikin Money Flow) ─────────────────────────────────────

export function cmf(candles, { period = 20 } = {}) {
  if (candles.length < period) return [];

  const result = [];
  for (let i = period - 1; i < candles.length; i++) {
    let sumMFV = 0, sumVol = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const c = candles[j];
      const range = c.high - c.low;
      const clv = range === 0 ? 0 : ((c.close - c.low) - (c.high - c.close)) / range;
      sumMFV += clv * c.volume;
      sumVol += c.volume;
    }
    result.push({ time: candles[i].time, value: sumVol === 0 ? 0 : sumMFV / sumVol });
  }
  return result;
}

// ─── VWMA (Volume Weighted Moving Average) ────────────────────────

export function vwma(candles, { period = 20 } = {}) {
  if (candles.length < period) return [];

  const result = [];
  for (let i = period - 1; i < candles.length; i++) {
    let sumPV = 0, sumV = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sumPV += candles[j].close * candles[j].volume;
      sumV += candles[j].volume;
    }
    result.push({ time: candles[i].time, value: sumV === 0 ? candles[i].close : sumPV / sumV });
  }
  return result;
}

// ─── Unified MA (configurable method, source, offset) ─────────────

const MA_METHODS = { sma, ema, wma, dema, tema, smma, hma, vwma };

export function ma(candles, { period = 20, method = 'sma', source = 'close', offset = 0 } = {}) {
  const fn = MA_METHODS[method] || sma;
  const raw = method === 'vwma' ? fn(candles, { period }) : fn(candles, { period, source });

  if (offset === 0) return raw;

  return raw.map((pt, i) => {
    const targetIdx = i + offset;
    if (targetIdx < 0 || targetIdx >= raw.length) return null;
    return { time: raw[targetIdx].time, value: pt.value };
  }).filter(Boolean);
}

// ─── Force Index (MT5-compatible) ─────────────────────────────────

export function forceIndex(candles, { period = 13, method = 'sma' } = {}) {
  if (candles.length < period + 1) return [];

  const fn = MA_METHODS[method] || sma;
  const maValues = fn(candles, { period, source: 'close' });
  if (maValues.length < 2) return [];

  const volMap = new Map();
  let volSum = 0;
  for (const c of candles) {
    volMap.set(c.time, c.volume);
    volSum += c.volume;
  }
  const noVolume = (volSum / candles.length) < 1;

  const result = [];
  for (let i = 1; i < maValues.length; i++) {
    const vol = noVolume ? 1 : (volMap.get(maValues[i].time) || 1);
    result.push({
      time: maValues[i].time,
      value: vol * (maValues[i].value - maValues[i - 1].value),
    });
  }
  return result;
}
